import { isErpApiSuccess } from "../erpClient";

export function createJangadIdempotencyKey() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export const isJangadsApiSuccess = (res) => isErpApiSuccess(res);

/** Extract rows from jangads list API (`data.items` or legacy shapes). */
export function extractJangadListRows(res) {
  const data = res?.data;
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.jangads)) return data.jangads;
  if (Array.isArray(data.jangad_details)) return data.jangad_details;
  if (data.id != null || data.voucher_number || data.doc_number) return [data];
  return [];
}

const splitCSVOrNewLines = (value) => {
  const s = String(value ?? "");
  return s
    .split(/[,|\n|\r]+/g)
    .map((x) => x.trim())
    .filter(Boolean);
};

export function createVisitorsPayload({ visitorNames, visitorMobiles, noOfVisitors }) {
  const names = splitCSVOrNewLines(visitorNames);
  const mobiles = splitCSVOrNewLines(visitorMobiles);

  const explicit = Number(noOfVisitors);
  const hasExplicit = Number.isFinite(explicit) && explicit > 0;
  const fromLists = Math.max(names.length, mobiles.length);
  // Prefer the larger of explicit count vs CSV rows so "Gaurav, Rutul" sends two visitors
  // even when No of Visitors is still 1.
  const normalizedCount = Math.max(fromLists, hasExplicit ? explicit : 0);

  const visitors = [];
  for (let i = 0; i < normalizedCount; i++) {
    visitors.push({
      name: names[i] || names[0] || "",
      mobile: mobiles[i] || mobiles[0] || "",
    });
  }

  // If neither names nor mobiles were provided, return empty array (backend can validate).
  if (visitors.length === 0) return [];
  return visitors;
}

export function mapJangadShowResponseToLegacyOrder(res) {
  // Response shape can vary; normalize to "row" first.
  const row =
    res?.data?.jangad_document ||
    res?.data?.jangad_details ||
    res?.data?.jangad_order_details ||
    res?.data?.jangad ||
    res?.data ||
    res;

  if (!row) return null;

  const docId =
    row.jangad_document_id ?? row.id ?? row.jangad_order_id ?? row.document_id;

  const voucher_number =
    row.doc_number ?? row.voucher_number ?? row.doc_no ?? String(docId ?? "");

  const status = row.status ?? row.jangad_status ?? "1";

  const expected_return =
    row.expected_return ?? row.exp_delivery ?? row.return_date ?? "";

  const partyType = row.party_type ?? row.group_type ?? "";
  const party_id = row.party_id ?? row.group_id ?? row.supplier_id ?? "";

  const visitorsArray = Array.isArray(row.visitors) ? row.visitors : [];
  let visitor_names =
    row.visitor_names ??
    visitorsArray.map((v) => v?.name).filter(Boolean).join(", ");
  let visitor_mobiles =
    row.visitor_mobiles ??
    visitorsArray.map((v) => v?.mobile).filter(Boolean).join(", ");

  // When API sends no visitors[], use selected party (customer / supplier / karigar) for display.
  const partyNameFallback = String(
    row.customer_name ??
      row.supplier_name ??
      row.name ??
      row.company_name ??
      row.karigar_name ??
      "",
  ).trim();
  const partyMobileFallback = String(
    row.customer_mobile ??
      row.supplier_mobile ??
      row.mobile ??
      "",
  ).trim();
  if (!String(visitor_names ?? "").trim() && partyNameFallback) {
    visitor_names = partyNameFallback;
  }
  if (!String(visitor_mobiles ?? "").trim() && partyMobileFallback) {
    visitor_mobiles = partyMobileFallback;
  }

  const items = Array.isArray(row.items) ? row.items : [];

  const jangad_entries = items.map((it, idx) => ({
    // UI/legacy expects `id` for item row.
    id: it.jangad_item_id ?? it.id ?? it.jangad_entry_id ?? idx,
    status: it.status ?? it.jangad_item_status ?? 1,
    product_id: it.product_id ?? it.productId ?? null,

    // Optional / best-effort fields used across existing UI.
    tag_no: it.tag_no ?? it.product_tag_no ?? it.tagNo ?? "",
    product_tag_no: it.product_tag_no ?? it.tag_no ?? "",
    product_group: it.product_group ?? it.product_type ?? it.product_group_id ?? "",
    product_group_name: it.product_group_name ?? it.product_group ?? "",
    pcs: it.pcs ?? "",
    // Socket payload uses `weight`; legacy UI displays gwt/current_weight.
    gwt: it.gwt ?? it.current_gwt ?? it.current_weight ?? it.weight ?? "",
    lwt: it.lwt ?? "",
    nwt: it.nwt ?? it.exp_weight ?? "",

    current_weight:
      it.current_weight ?? it.gwt ?? it.weight ?? "",
    exp_weight: it.exp_weight ?? it.nwt ?? "",

    item_remarks: it.item_remarks ?? "",
    remarks: it.remarks ?? "",

    // Receive fields (if backend provides them via show())
    received_gwt: it.received_gwt ?? "",
    received_nwt: it.received_nwt ?? "",
    received_date: it.received_date ?? "",
    received_by: it.received_by ?? "",
    received_no_of_visitors: it.received_no_of_visitors ?? "",
    received_visitor_names: it.received_visitor_names ?? "",
    received_visitor_mobiles: it.received_visitor_mobiles ?? "",
    received_remarks: it.received_remarks ?? "",
  }));

  return {
    ...row,
    id: docId ?? row.id,
    voucher_number,
    exp_delivery: expected_return,
    status,
    // Legacy UI uses group_type/group_id for lookups.
    group_type:
      typeof partyType === "string" ? partyType.toLowerCase() : partyType,
    group_id: party_id,
    // For existing JangadTable display helpers.
    name: row.supplier_name ?? row.name ?? row.company_name ?? row.karigar_name ?? "",
    company_name:
      row.company_name ?? row.supplier_name ?? row.name ?? row.karigar_name ?? "",
    karigar_name:
      row.karigar_name ?? row.supplier_name ?? row.name ?? row.company_name ?? "",
    mobile:
      row.customer_mobile ?? row.supplier_mobile ?? row.mobile ?? "",
    contact_number:
      row.customer_mobile ?? row.supplier_mobile ?? row.mobile ?? "",
    created_by_name: row.created_by_name ?? row.by_user_name ?? "",
    employee_name:
      row.employee_name ??
      row.created_by_name ??
      row.by_user_name ??
      "",

    visitor_names: visitor_names ?? "",
    visitor_mobiles: visitor_mobiles ?? "",

    jangad_entries,
  };
}

export function normalizeListJangadsRows(res) {
  if (!isJangadsApiSuccess(res)) return [];
  return extractJangadListRows(res).map((r) => ({
    id: r.id ?? r.jangad_document_id,
    voucher_number: r.voucher_number ?? r.doc_number ?? "",
    exp_delivery:
      r.expected_return ?? r.exp_delivery ?? r.doc_date ?? r.return_date ?? "",
    status: r.status,
    grand_total: r.grand_total ?? 0,
    supplier_name: r.supplier_name ?? r.customer_name ?? "",
    supplier_mobile: r.supplier_mobile ?? r.customer_mobile ?? "",
  }));
}

/**
 * Map one row from POST /jangads type=list (enriched list) to the same legacy shape
 * used by JangadTable / EditJangad — no show() call required for the grid.
 */
export function mapJangadListRowToLegacyOrder(row) {
  if (!row) return null;

  const docId = row.jangad_document_id ?? row.id;
  if (docId == null || docId === "") return null;

  const items = Array.isArray(row.items) ? row.items : [];
  const jangad_entries = items.map((it, idx) => ({
    id: it.jangad_item_id ?? it.id ?? it.jangad_entry_id ?? idx,
    status: it.status ?? it.jangad_item_status ?? 1,
    tag_no: it.tag_no ?? it.product_tag_no ?? it.tagNo ?? "",
    product_tag_no: it.product_tag_no ?? it.tag_no ?? "",
    product_group: it.product_group ?? it.product_type ?? it.product_group_id ?? "",
    product_group_name: it.product_group_name ?? it.product_group ?? "",
    product_id: it.product_id,
    pcs: it.pcs ?? "",
    gwt: it.gwt ?? it.current_weight ?? "",
    lwt: it.lwt ?? "",
    nwt: it.nwt ?? it.exp_weight ?? "",
    karigar_id: it.karigar_id,
    given_emp_id: it.given_emp_id,
  }));

  const partyType = String(row.party_type ?? row.group_type ?? "").toLowerCase();
  const partyName =
    row.customer_name ||
    row.supplier_name ||
    row.name ||
    "";

  const voucher_number =
    row.voucher_number ?? row.doc_number ?? row.doc_no ?? String(docId ?? "");

  const exp_delivery =
    row.expected_return ?? row.exp_delivery ?? row.doc_date ?? row.return_date ?? "";

  return {
    ...row,
    id: docId,
    voucher_number,
    exp_delivery,
    group_type: partyType,
    group_id: row.party_id ?? row.group_id ?? "",
    name: partyName,
    company_name: row.customer_name ?? row.company_name ?? "",
    karigar_name: row.supplier_name ?? row.karigar_name ?? "",
    mobile:
      row.customer_mobile ?? row.supplier_mobile ?? row.mobile ?? "",
    contact_number:
      row.customer_mobile ?? row.supplier_mobile ?? row.mobile ?? "",
    visitor_mobiles:
      row.visitor_mobiles ??
      row.customer_mobile ??
      row.supplier_mobile ??
      row.mobile ??
      "",
    employee_name:
      row.created_by_name ?? row.employee_name ?? row.by_user_name ?? "",
    created_by_name: row.created_by_name ?? "",
    jangad_entries,
  };
}

